import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useGlobalSettings } from '../contexts/GlobalSettingsContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import supabase from '../supabase/supabase';
import Logo from '../components/Logo';

const { FiShield, FiLock, FiSave, FiLogOut, FiSettings, FiStar, FiZap, FiTarget } = FiIcons;

const AdminPage = () => {
  const { settings, updateSettings } = useGlobalSettings();
  const { refreshQuotas } = useSubscription();
  const [authStep, setAuthStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [saveStatus, setSaveStatus] = useState('');
  
  const [venues, setVenues] = useState([]);
  const [formData, setFormData] = useState({
    productionUrl: '',
    promotedAdsCount: 3,
    quota_free: 0,
    quota_standard: 3,
    quota_pro: 10
  });

  useEffect(() => {
    if (authStep === 'authenticated') {
      loadSettings();
      fetchVenues();
    }
  }, [authStep]);

  const loadSettings = async () => {
    const { data } = await supabase.from('admin_settings_1780601000000').select('*');
    const dbSettings = {};
    data?.forEach(s => dbSettings[s.key] = s.value);

    setFormData({
      productionUrl: settings.productionUrl,
      promotedAdsCount: dbSettings.promoted_ads_count || 3,
      quota_free: dbSettings.quota_landmarks_free || 0,
      quota_standard: dbSettings.quota_landmarks_standard || 3,
      quota_pro: dbSettings.quota_landmarks_pro || 10
    });
  };

  const fetchVenues = async () => {
    const { data } = await supabase.from('venues_1780591882954').select('*');
    setVenues(data || []);
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setSaveStatus('saving');
    
    // Persist all to Supabase
    await supabase.from('admin_settings_1780601000000').upsert([
      { key: 'promoted_ads_count', value: formData.promotedAdsCount },
      { key: 'quota_landmarks_free', value: formData.quota_free },
      { key: 'quota_landmarks_standard', value: formData.quota_standard },
      { key: 'quota_landmarks_pro', value: formData.quota_pro }
    ]);

    await updateSettings({ productionUrl: formData.productionUrl });
    await refreshQuotas();
    
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus(''), 3000);
  };

  if (authStep !== 'authenticated') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl w-full max-w-md shadow-2xl">
          <Logo className="justify-center mb-8" />
          {authStep === 'email' ? (
            <form onSubmit={(e) => { e.preventDefault(); if(email === 'weboffice.ro@gmail.com') setAuthStep('otp'); }} className="space-y-4">
              <input type="email" placeholder="Admin Email" value={email} onChange={e => setEmail(e.target.value)} className="w-full p-3 border rounded-xl" />
              <button className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold">Verify</button>
            </form>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); if(otp === '123456') setAuthStep('authenticated'); }} className="space-y-4">
              <input type="text" placeholder="OTP 123456" value={otp} onChange={e => setOtp(e.target.value)} className="w-full p-3 border rounded-xl text-center font-bold" />
              <button className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold">Login</button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-gray-900 text-white p-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Logo dark={true} />
          <div className="flex gap-4">
            <button onClick={() => setActiveTab('general')} className={`px-4 py-2 rounded-lg text-sm ${activeTab === 'general' ? 'bg-blue-600' : 'hover:bg-gray-800'}`}>Settings</button>
            <button onClick={() => setActiveTab('promotions')} className={`px-4 py-2 rounded-lg text-sm ${activeTab === 'promotions' ? 'bg-blue-600' : 'hover:bg-gray-800'}`}>VIP Ads</button>
            <button onClick={() => setAuthStep('email')} className="p-2 text-gray-400 hover:text-white"><SafeIcon icon={FiLogOut} /></button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto py-10 px-4">
        {activeTab === 'general' ? (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-sm border p-8">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <SafeIcon icon={FiSettings} className="text-blue-600" /> Platform Configuration V2
              </h2>
              <form onSubmit={handleSettingsSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Production URL</label>
                    <input type="text" value={formData.productionUrl} onChange={e => setFormData({...formData, productionUrl: e.target.value})} className="w-full p-3 border rounded-xl" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Promoted Slots (X)</label>
                    <input type="number" value={formData.promotedAdsCount} onChange={e => setFormData({...formData, promotedAdsCount: e.target.value})} className="w-full p-3 border rounded-xl" />
                  </div>
                </div>

                <div className="pt-6 border-t">
                  <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiTarget} className="text-blue-600" /> Subscription Quotas (Landmarks)
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-xl border">
                      <label className="block text-[10px] font-black text-gray-400 uppercase mb-2">Free Plan</label>
                      <input type="number" value={formData.quota_free} onChange={e => setFormData({...formData, quota_free: e.target.value})} className="w-full p-2 border rounded-lg font-bold" />
                    </div>
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                      <label className="block text-[10px] font-black text-blue-400 uppercase mb-2">Standard Plan</label>
                      <input type="number" value={formData.quota_standard} onChange={e => setFormData({...formData, quota_standard: e.target.value})} className="w-full p-2 border rounded-lg font-bold text-blue-700" />
                    </div>
                    <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
                      <label className="block text-[10px] font-black text-indigo-400 uppercase mb-2">Pro Plan</label>
                      <input type="number" value={formData.quota_pro} onChange={e => setFormData({...formData, quota_pro: e.target.value})} className="w-full p-2 border rounded-lg font-bold text-indigo-700" />
                    </div>
                  </div>
                </div>

                <button disabled={saveStatus === 'saving'} className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg hover:bg-blue-700 transition-all">
                  <SafeIcon icon={FiSave} /> {saveStatus === 'saved' ? 'Saved Successfully!' : 'Save Version 2.0 Settings'}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
             <div className="p-6 border-b bg-gray-50">
               <h2 className="font-bold flex items-center gap-2"><SafeIcon icon={FiStar} className="text-yellow-500" /> Manage Promotions</h2>
             </div>
             <table className="w-full text-left">
               <thead><tr className="text-xs text-gray-400 uppercase border-b bg-gray-50/50"><th className="p-4">Venue</th><th className="p-4">Owner</th><th className="p-4 text-center">Status</th></tr></thead>
               <tbody className="divide-y">
                 {venues.map(v => (
                   <tr key={v.id}>
                     <td className="p-4 font-bold text-sm">{v.name}</td>
                     <td className="p-4 text-xs text-gray-500 font-mono">{v.owner_id.slice(0,8)}...</td>
                     <td className="p-4 text-center">
                        <button onClick={async () => {
                          await supabase.from('venues_1780591882954').update({ is_promoted: !v.is_promoted }).eq('id', v.id);
                          fetchVenues();
                        }} className={`px-4 py-1 rounded-full text-[10px] font-bold ${v.is_promoted ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' : 'bg-gray-100 text-gray-400 border border-gray-200'}`}>
                          {v.is_promoted ? 'VIP' : 'Standard'}
                        </button>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;